//! Helpers for moving the cursor past hidden columns and rows. Excel never
//! lands the cursor on a hidden column or row, so any keyboard navigation that
//! computes a target coordinate needs to step past hidden entries first.

import { sheets } from '@/app/grid/controller/Sheets';
import type { JsCoordinate } from '@/app/quadratic-core-types';

/// Steps past hidden columns in the given direction. Returns the first visible
/// column, or undefined when every column through column 1 is hidden.
export function skipHiddenColumns(column: number, step: 1 | -1): number | undefined {
  const offsets = sheets.sheet.offsets;
  let c = column;
  while (c >= 1 && offsets.isColumnHidden(c)) {
    c += step;
  }
  return c >= 1 ? c : undefined;
}

/// Steps past hidden rows in the given direction. Returns the first visible
/// row, or undefined when every row through row 1 is hidden.
export function skipHiddenRows(row: number, step: 1 | -1): number | undefined {
  const offsets = sheets.sheet.offsets;
  let r = row;
  while (r >= 1 && offsets.isRowHidden(r)) {
    r += step;
  }
  return r >= 1 ? r : undefined;
}

/// Applies a cursor delta (Enter/Tab and friends), stepping past hidden
/// columns/rows so the cursor lands on the next visible cell. An axis stays put
/// when every candidate in that direction is hidden.
export function applyDeltaSkippingHidden(position: JsCoordinate, deltaX: number, deltaY: number): JsCoordinate {
  let { x, y } = position;
  if (deltaX !== 0) {
    x = skipHiddenColumns(x + deltaX, deltaX > 0 ? 1 : -1) ?? x;
  }
  if (deltaY !== 0) {
    y = skipHiddenRows(y + deltaY, deltaY > 0 ? 1 : -1) ?? y;
  }
  return { x, y };
}
